import React from 'react'
import { clsx } from 'clsx'

import type { PasswordStrengthResult } from '~/hooks/use-password-strength'

interface PasswordStrengthIndicatorProps {
  isDirty: boolean
  strength: PasswordStrengthResult
}

const getStrengthText = (score: number): string => {
  switch (score) {
    case 0:
    case 1:
    case 2:
      return 'Muy débil'
    case 3:
      return 'Buena'
    case 4:
      return 'Muy fuerte'
    default:
      return 'Muy débil'
  }
}

const getProgressBarClasses = (score: number): string => {
  const baseClasses = 'h-full rounded-sm transition-all duration-300 ease-in-out'

  const widthClass = `w-[${Math.max(score * 25, 5)}%]`

  let colorClass = ''

  switch (score) {
    case 0:
    case 1:
    case 2:
      colorClass = 'bg-red-500'
      break
    case 3:
      colorClass = 'bg-yellow-500'
      break
    default:
      colorClass = 'bg-emerald-500'
  }

  return clsx(baseClasses, widthClass, colorClass)
}

const getStrengthLabelClasses = (score: number): string => {
  const baseClasses = 'text-xs font-medium mt-1'

  let colorClass = ''

  switch (score) {
    case 0:
    case 1:
    case 2:
      colorClass = 'text-red-600'
      break
    case 3:
      colorClass = 'text-yellow-600'
      break
    default:
      colorClass = 'text-emerald-600'
  }

  return clsx(baseClasses, colorClass)
}

function PasswordStrengthIndicator(props: PasswordStrengthIndicatorProps) {
  const { isDirty, strength } = props

  if (!isDirty) {
    return null
  }

  return (
    <>
      <div className="mt-2 h-1 w-full overflow-hidden rounded-sm bg-gray-200">
        <div className={getProgressBarClasses(strength.score)} />
      </div>
      <div className={getStrengthLabelClasses(strength.score)}>Seguridad: {getStrengthText(strength.score)}</div>
      <div className="mt-2 text-xs leading-5">
        {strength.feedback.warning && <div className="mb-1 font-medium text-red-600">{strength.feedback.warning}</div>}
        {strength.feedback.suggestions.length > 0 && (
          <div className="text-gray-500">
            {strength.feedback.suggestions.map((suggestion, index) => (
              <div key={index} className="mb-0.5 before:text-gray-400 before:content-['•_']">
                {suggestion}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

export default PasswordStrengthIndicator
